// OPTIMIZATION: Use centralized Parse initialization
import Parse from '../../../services/initializeParse'
import { useEffect, useRef, useCallback } from 'react'
import { BACK4APP_CONFIG } from '../../../config/back4app.config'

let liveQueryClient: Parse.LiveQueryClient | null = null
let currentSessionToken: string | undefined | null = null
let clientRefCount = 0
let closeTimer: ReturnType<typeof setTimeout> | null = null
let clientIsOpen = false

const CLOSE_DELAY_MS = 1500

// A client closed on purpose (token change, idle close) never emits 'open' again: Parse's close()
// sets DISCONNECTED and its reconnect logic returns early. Whoever is still waiting for 'open' on
// it must be released, or its pending subscription entry would hang and swallow later subscribes.
const closedClients = new WeakSet<object>()
const openWaiters = new Map<object, Set<() => void>>()

function closeClient(client: Parse.LiveQueryClient): void {
  closedClients.add(client)
  void client.close()
  if (liveQueryClient === client) {
    liveQueryClient = null
    currentSessionToken = null
    clientIsOpen = false
  }
  openWaiters.get(client)?.forEach((release) => release())
  openWaiters.delete(client)
}

/** Resolves true once the client is open, false if it is closed on purpose first. */
function waitForClientOpen(client: Parse.LiveQueryClient): Promise<boolean> {
  if (closedClients.has(client)) return Promise.resolve(false)
  if (clientIsOpen) {
    return Promise.resolve(true)
  }
  return new Promise((resolve) => {
    let settled = false
    const waiters = openWaiters.get(client) ?? new Set<() => void>()
    openWaiters.set(client, waiters)
    const release = () => {
      if (settled) return
      settled = true
      waiters.delete(release)
      resolve(false)
    }
    const onOpen = () => {
      if (settled) return
      settled = true
      waiters.delete(release)
      clientIsOpen = true
      resolve(true)
    }
    waiters.add(release)
    client.on('open', onOpen)
  })
}

const getClient = (sessionToken?: string): Parse.LiveQueryClient => {
    // If the session token has changed, tear down the old client before creating a new one.
    if (sessionToken !== currentSessionToken && liveQueryClient) {
        console.log('[LiveQueryV2] Session token changed, recreating client.')
        closeClient(liveQueryClient)
    }

    if (liveQueryClient) {
        return liveQueryClient;
    }

    liveQueryClient = new Parse.LiveQueryClient({
        applicationId: BACK4APP_CONFIG.applicationId,
        serverURL: BACK4APP_CONFIG.liveQueryServerURL,
        javascriptKey: BACK4APP_CONFIG.javascriptKey,
        masterKey: undefined,
        sessionToken: sessionToken,
        installationId: undefined,
    });

    liveQueryClient.open();
    currentSessionToken = sessionToken;
    clientIsOpen = false

    liveQueryClient.on('open', () => {
        clientIsOpen = true
    });

    liveQueryClient.on('close', () => {
        liveQueryClient = null;
        currentSessionToken = null;
        clientIsOpen = false
    });

    liveQueryClient.on('error', (error) => {
        console.error('LiveQuery client error:', error);
    });

    return liveQueryClient;
}

export const useLiveQueryV2 = (sessionToken?: string) => {
  const subscriptions = useRef<Map<string, any>>(new Map()).current

  useEffect(() => {
    if (!sessionToken) {
      return;
    }

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    clientRefCount++;
    getClient(sessionToken);

    return () => {
      clientRefCount--;
      if (clientRefCount === 0) {
        closeTimer = setTimeout(() => {
          if (clientRefCount === 0 && liveQueryClient) {
            closeClient(liveQueryClient)
          }
        }, CLOSE_DELAY_MS);
      }
    };
  }, [sessionToken]);

  // Subscriptions still being set up. Chat mounts on every page and re-runs its effects, so the
  // same query is requested again before the first LiveQuery subscribe has resolved; without this
  // each repeat opened a duplicate subscription whose events were all billed. An unsubscribe that
  // arrives mid-setup marks the entry cancelled so the subscription is dropped once it exists.
  type PendingEntry = {
    cancelled: boolean
    promise: Promise<void>
    client: Parse.LiveQueryClient | null
  }
  const pending = useRef<Map<string, PendingEntry>>(new Map()).current

  const subscribe = useCallback(
    <T extends Parse.Object>(
      query: Parse.Query<T>,
      callback: (object: T, event: 'create' | 'update' | 'delete' | 'enter' | 'leave') => void,
      subscriptionKey: string,
    ): Promise<void> => {
      if (!sessionToken) {
        return Promise.resolve()
      }
      if (subscriptions.has(subscriptionKey)) {
        return Promise.resolve()
      }
      const inFlight = pending.get(subscriptionKey)
      if (inFlight) {
        // Its client may have been closed this very tick, before the waiter's cleanup has run.
        if (!inFlight.client || !closedClients.has(inFlight.client)) return inFlight.promise
        inFlight.cancelled = true
        pending.delete(subscriptionKey)
      }

      const entry = {
        cancelled: false,
        promise: Promise.resolve(),
        client: null as Parse.LiveQueryClient | null,
      }
      const setup = async () => {
        const client = getClient(sessionToken)
        entry.client = client
        const opened = await waitForClientOpen(client)
        if (!opened || entry.cancelled) return
        const subscription = await client.subscribe(query)
        if (!subscription) {
          console.error(`Failed to create subscription for key "${subscriptionKey}"`)
          return
        }
        if (entry.cancelled) {
          subscription.unsubscribe()
          return
        }
        subscriptions.set(subscriptionKey, subscription)

        subscription.on('create', (object) => {
          console.log(`[LiveQueryV2] ${subscriptionKey} - create event:`, object.id, object.className)
          callback(object as T, 'create')
        })
        subscription.on('update', (object) => {
          console.log(`[LiveQueryV2] ${subscriptionKey} - update event:`, object.id, object.className)
          callback(object as T, 'update')
        })
        subscription.on('delete', (object) => {
          console.log(`[LiveQueryV2] ${subscriptionKey} - delete event:`, object.id, object.className)
          callback(object as T, 'delete')
        })
        subscription.on('enter', (object) => {
          console.log(`[LiveQueryV2] ${subscriptionKey} - enter event:`, object.id, object.className)
          callback(object as T, 'enter')
        })
        subscription.on('leave', (object) => {
          console.log(`[LiveQueryV2] ${subscriptionKey} - leave event:`, object.id, object.className)
          callback(object as T, 'leave')
        })
      }
      entry.promise = setup().finally(() => {
        if (pending.get(subscriptionKey) === entry) pending.delete(subscriptionKey)
      })
      pending.set(subscriptionKey, entry)
      return entry.promise
    },
    [subscriptions, pending, sessionToken],
  )

  const unsubscribe = useCallback(
    (subscriptionKey: string) => {
      const inFlight = pending.get(subscriptionKey)
      if (inFlight) {
        inFlight.cancelled = true
        pending.delete(subscriptionKey)
      }
      const subscription = subscriptions.get(subscriptionKey)
      if (subscription) {
        subscription.unsubscribe()
        subscriptions.delete(subscriptionKey)
      }
    },
    [subscriptions, pending],
  )

  const unsubscribeAll = useCallback(() => {
    pending.forEach((entry) => {
      entry.cancelled = true
    })
    pending.clear()
    subscriptions.forEach((sub) => {
      sub.unsubscribe()
    })
    subscriptions.clear()
  }, [subscriptions, pending])

  useEffect(() => {
    return () => {
      unsubscribeAll()
    }
  }, [unsubscribeAll])

  return { subscribe, unsubscribe, unsubscribeAll }
}
